/**
 * Switch the signed-in session to another account linked to the same
 * phone number — in this school or in another school (see
 * GET /api/users/associated). Every account switcher in the portal goes
 * through here so a cross-school switch always swaps the school context
 * along with the token.
 *
 * Ends in a full page load on purpose: it drops every in-memory cache
 * (permissions, profile, school branding) that belonged to the previous
 * account.
 */

const ROLE_DASHBOARD = {
  superadmin: '/superadmin-dashboard',
  schooladmin: '/admin-dashboard',
  teacher: '/teacher-dashboard',
  student: '/student-dashboard',
};

const destinationFor = (target) => {
  // Same routing as a fresh login: an expired school sends its admin to
  // renew and everyone else to the expired notice.
  if (target.school?.isSubscriptionExpired) {
    return target.role === 'schooladmin' ? '/renew' : '/expired';
  }
  return ROLE_DASHBOARD[target.role] || '/staff-dashboard';
};

/** True when `account` belongs to a different school than the session. */
export const isOtherSchool = (account, currentSchoolCode) =>
  Boolean(
    account?.school?.schoolCode &&
      currentSchoolCode &&
      account.school.schoolCode.toUpperCase() !== String(currentSchoolCode).toUpperCase(),
  );

export const switchToAccount = (target) => {
  if (!target?.token || typeof window === 'undefined') return;

  sessionStorage.setItem('authToken', target.token);
  sessionStorage.setItem('userRole', target.role);
  if (target.school?.schoolCode) {
    sessionStorage.setItem('schoolCode', target.school.schoolCode);
    sessionStorage.setItem('schoolInfo', JSON.stringify(target.school));
  }
  // The cached list was minted for the previous session; the next page
  // refetches it with the new token.
  sessionStorage.removeItem('associatedUsers');

  const maxAge = 60 * 60 * 24;
  const secureFlag = window.location.protocol === 'https:' ? 'Secure;' : '';
  document.cookie = `kayease_token=${target.token}; Path=/; Max-Age=${maxAge}; ${secureFlag}SameSite=Strict`;

  window.location.href = destinationFor(target);
};
